import { SavingStatus } from 'src/models/common/SavingStatus';
import { AllocationGroupFormula } from 'src/models/tp-allocation/AllocationGroup';

/** Half of the last shown digit (0.001%), so a total shown as 100.000% counts as full. */
const FULL_ALLOCATION_TOLERANCE = 0.000005;

/**
 * How a group's total share from the last run compares with what the allocation sheet check downstream
 * accepts: it rejects a total above 100% or below 0, and passes anything from 0 to 100%.
 */
export enum GroupShareStatus {
    /** The group allocates the whole balance. */
    FULL = 'FULL',
    /** The group allocates part of the balance; accepted downstream. */
    PARTIAL = 'PARTIAL',
    /** Above 100% or below 0; rejected downstream. */
    OUT_OF_RANGE = 'OUT_OF_RANGE',
}

const toNumber = (value?: string): number | undefined => {
    if (value == null || value.trim() === '') return undefined;
    const n = Number(value);
    return Number.isNaN(n) ? undefined : n;
};

/**
 * Adds up the outputs of a group's formulas from the last run. Formulas without a value count as 0.
 * @param formulas The group's formulas.
 * @returns The total ratio, or undefined when no formula has a value yet (the sheet has not run).
 */
export function groupShareTotal(formulas: AllocationGroupFormula[]): number | undefined {
    const values = formulas.map((f) => toNumber(f.value)).filter((v): v is number => v !== undefined);
    return values.length === 0 ? undefined : values.reduce((sum, v) => sum + v, 0);
}

/** Classifies a group's total ratio; a total that shows as 100.000% counts as full. */
export function groupShareStatus(total: number): GroupShareStatus {
    if (Math.abs(total - 1) < FULL_ALLOCATION_TOLERANCE) return GroupShareStatus.FULL;
    return total < 0 || total > 1 ? GroupShareStatus.OUT_OF_RANGE : GroupShareStatus.PARTIAL;
}

/** True while a group has a formula that is new or edited and not yet saved; its last-run total is then stale. */
export function hasUnsavedFormulas(formulas: AllocationGroupFormula[]): boolean {
    return formulas.some((f) => f.isNew || f.savingStatus === SavingStatus.Unsaved);
}
